import type { ProcessedMessage } from '../utils/message-processing';
import { mergeAdjacentReasoningRenderItems } from './chat-reasoning-merge';
import {
  getAttachedConfirmations,
  getConfirmationAnchorId,
  indexConfirmationAttachments,
  isConfirmationAnchorPart,
} from './chat-message-list-confirmations';
import { consolidateReadSearchRenderItems } from './chat-message-list-consolidation';
import {
  appendSubAgentAssistantText,
  canMergeSubAgentContinuation,
  canNestInvestigationInSubAgent,
  createRenderablePart,
  isAssistantResponsePart,
  isSubAgentGroupRunning,
  isSubAgentPart,
  splitContinuationMessageParts,
} from './chat-message-list-subagent-groups';
import {
  assistantTextMatchesOpenGroup,
  getLastCodeAnalystWriteupKeyByParent,
  isCopilotMainAgentOrigin,
  resolveAssistantTextPlacement,
  shouldNestSubAgentAssistantPart,
} from './chat-message-list-subagent-text';
import { readBackgroundUpdateAgentTypes } from './background-update-agent';
import {
  getParentToolCallIdFromPart,
  getToolCallIdFromPart,
  getToolNameFromPart,
} from './chat-message-part-accessors';
import { consumeUserMessageForChatRender } from './chat-message-list-user-render';
import type {
  ChatRenderItem,
  RenderablePart,
  SubAgentExecutionGroup,
} from './chat-message-list-types';

export {
  getToolNameFromPart,
  isObjectResultPart,
  isPendingHITLPart,
  isRunningPart,
} from './chat-message-part-accessors';
export {
  buildConsolidatedSummary,
  getConsolidatedLiveLabel,
  isConsolidatablePart,
  partitionConsolidatableParts,
} from './chat-message-list-consolidation';
export {
  partitionSubAgentChildParts,
  expandSubAgentChildUnits,
  splitSubAgentVisualTimeline,
  withRunningWorkTail,
  ensureBackgroundRunningHeadline,
  timelineWithParentHeadline,
  collectPartsFromRenderUnits,
} from './chat-message-list-analyzer-groups';
export {
  buildSubAgentSummary,
  isSubAgentGroupRunning,
  isSubAgentGroupStopped,
  resolveSubAgentGroupExecution,
  splitContinuationMessageParts,
} from './chat-message-list-subagent-groups';
export type { SubAgentVisualSegment } from './chat-message-list-analyzer-groups';
export type { ConsolidationSurface } from './chat-message-list-consolidation';
export type {
  ChatRenderItem,
  ChatRenderUnit,
  RenderablePart,
  SubAgentExecutionGroup,
} from './chat-message-list-types';

export function buildChatRenderItems({
  processedMessages,
  stableIsLoading,
  isFeedbackEnabled,
  noAssistantHeader,
}: {
  processedMessages: ProcessedMessage[];
  stableIsLoading: boolean;
  isFeedbackEnabled?: boolean;
  noAssistantHeader?: boolean;
}): ChatRenderItem[] {
  const renderItems: ChatRenderItem[] = [];
  const seenBackgroundUpdateToolCallIds = new Set<string>();
  const confirmationIndex = indexConfirmationAttachments(processedMessages);
  const consumedConfirmationKeys = new Set<string>();
  let pendingSubAgentGroup: SubAgentExecutionGroup | null = null;
  let pendingSubAgentGroupWasRendered = false;
  let lastHITLSubAgentGroup: SubAgentExecutionGroup | null = null;
  let lastHITLSubAgentGroupMessageIndex: number | null = null;
  const subAgentChildrenAreLinked = processedMessages.some((processedMessage) =>
    processedMessage.parts.some(
      (part) =>
        Boolean(getParentToolCallIdFromPart(part)) ||
        isCopilotMainAgentOrigin(part),
    ),
  );

  const clearResumableHITLSubAgentGroupIfLaterMessage = ({
    messageIndex,
  }: {
    messageIndex: number;
  }) => {
    if (
      lastHITLSubAgentGroup &&
      lastHITLSubAgentGroupMessageIndex !== null &&
      messageIndex > lastHITLSubAgentGroupMessageIndex
    ) {
      if (pendingSubAgentGroup === lastHITLSubAgentGroup) {
        pendingSubAgentGroup = null;
        pendingSubAgentGroupWasRendered = false;
      }

      lastHITLSubAgentGroup = null;
      lastHITLSubAgentGroupMessageIndex = null;
    }
  };

  const subAgentGroups: SubAgentExecutionGroup[] = [];

  const findOwnerSubAgentGroup = (
    item: RenderablePart,
  ): SubAgentExecutionGroup | undefined => {
    const parentToolCallId = getParentToolCallIdFromPart(item.processedPart);

    if (!parentToolCallId) {
      return undefined;
    }

    return subAgentGroups.find((group) =>
      [group.parent, ...group.continuationParents, ...group.children].some(
        (member) =>
          getToolCallIdFromPart(member.processedPart) === parentToolCallId,
      ),
    );
  };

  const appendRenderablePart = (item: RenderablePart) => {
    const ownerGroup = findOwnerSubAgentGroup(item) ?? pendingSubAgentGroup;

    if (ownerGroup) {
      ownerGroup.children.push(item);

      return ownerGroup;
    }

    renderItems.push({ kind: 'part', item });

    return null;
  };

  const appendPartInStoryOrder = ({
    item,
    messageIndex,
  }: {
    item: RenderablePart;
    messageIndex: number;
  }) => {
    const ownerGroup =
      findOwnerSubAgentGroup(item) ??
      (subAgentChildrenAreLinked ? null : pendingSubAgentGroup);

    if (ownerGroup) {
      ownerGroup.children.push(item);

      return;
    }

    if (pendingSubAgentGroup) {
      clearResumableHITLSubAgentGroupIfLaterMessage({ messageIndex });
      flushSubAgentGroup({ messageIndex });
    }

    renderItems.push({ kind: 'part', item });
  };

  const appendConfirmationAttachments = (item: RenderablePart) => {
    if (!isConfirmationAnchorPart(item.processedPart)) {
      return;
    }

    const attachedConfirmations = getAttachedConfirmations({
      confirmationIndex,
      anchorId: getConfirmationAnchorId(item.processedPart),
      anchorMessageIndex: item.processedMessage.index,
      consumedConfirmationKeys,
    });

    if (attachedConfirmations.length === 0) {
      return;
    }

    const targetGroup = pendingSubAgentGroup;

    if (targetGroup) {
      targetGroup.children.push(...attachedConfirmations);
      targetGroup.hasAttachedHITLResponse = true;

      return;
    }

    renderItems.push(
      ...attachedConfirmations.map((attachedItem) => ({
        kind: 'part' as const,
        item: attachedItem,
      })),
    );
  };

  const flushSubAgentGroup = ({ messageIndex }: { messageIndex: number }) => {
    if (!pendingSubAgentGroup) return;
    if (pendingSubAgentGroup.hasAttachedHITLResponse) {
      lastHITLSubAgentGroup = pendingSubAgentGroup;
      lastHITLSubAgentGroupMessageIndex = messageIndex;
    }

    if (!pendingSubAgentGroupWasRendered) {
      renderItems.push({
        kind: 'sub-agent-group',
        group: pendingSubAgentGroup,
      });
    }

    pendingSubAgentGroup = null;
    pendingSubAgentGroupWasRendered = false;
  };

  const clearHitlResume = () => {
    lastHITLSubAgentGroup = null;
    lastHITLSubAgentGroupMessageIndex = null;
  };

  const pushItem = (item: ChatRenderItem) => {
    renderItems.push(item);
  };

  for (const processedMessage of processedMessages) {
    const { message: uiMessage, index: uiMessageIndex } = processedMessage;

    if (uiMessage.role === 'user') {
      consumeUserMessageForChatRender({
        processedMessage,
        consumedConfirmationKeys,
        pendingSubAgentGroup,
        appendRenderablePart,
        flushSubAgentGroup,
        pushItem,
        clearHitlResume,
      });
      continue;
    }

    const resolvedNoHeader = noAssistantHeader ?? uiMessageIndex > 0;
    const autoCollapseToolCalls =
      !stableIsLoading || !processedMessage.isLastAssistantMessage;

    if (processedMessage.isBackgroundUpdate) {
      flushSubAgentGroup({ messageIndex: uiMessageIndex });
      const agentTypes = readBackgroundUpdateAgentTypes(uiMessage);
      const messageKey = uiMessage.id ?? uiMessageIndex;

      agentTypes.forEach((entry, index) => {
        if (entry.toolCallId) {
          if (seenBackgroundUpdateToolCallIds.has(entry.toolCallId)) {
            return;
          }

          seenBackgroundUpdateToolCallIds.add(entry.toolCallId);
        }

        renderItems.push({
          kind: 'background-update-separator',
          agentType: entry.agentType,
          stopped: entry.stopped,
          separatorKey: `background-update-${messageKey}-${entry.agentType}-${index}`,
        });
      });
    }

    const writeupKeyByParent = getLastCodeAnalystWriteupKeyByParent({
      parts: processedMessage.parts,
      isCodeAnalystParent: (part) =>
        isSubAgentPart(part) &&
        getToolNameFromPart(part)?.replace(/_/g, '-').toLowerCase() ===
          'code-analyst',
      isOtherSubAgentParent: (part) =>
        isSubAgentPart(part) &&
        getToolNameFromPart(part)?.replace(/_/g, '-').toLowerCase() !==
          'code-analyst',
    });
    const hasMainAgentTextInMessage = processedMessage.parts.some(
      (part) =>
        part.kind !== 'skip' &&
        isAssistantResponsePart(part) &&
        !shouldNestSubAgentAssistantPart({
          processedPart: part,
          lastCodeAnalystWriteupKeys: new Set(writeupKeyByParent.values()),
        }),
    );

    const { narrativeParts, subAgentResumeParts } =
      splitContinuationMessageParts({
        parts: processedMessage.parts,
      });

    const suppressResumeIntoPriorHITLGroup =
      hasMainAgentTextInMessage &&
      subAgentResumeParts.length > 0 &&
      lastHITLSubAgentGroup !== null &&
      uiMessageIndex > (lastHITLSubAgentGroupMessageIndex ?? -1);

    if (suppressResumeIntoPriorHITLGroup) {
      clearResumableHITLSubAgentGroupIfLaterMessage({
        messageIndex: uiMessageIndex,
      });
    }

    const partsToProcess = suppressResumeIntoPriorHITLGroup
      ? [...narrativeParts, ...subAgentResumeParts]
      : processedMessage.parts.filter((part) => part.kind !== 'skip');

    let clearedForSubAgentResumePass = false;
    let processedPartIndex = 0;

    for (const processedPart of partsToProcess) {
      if (
        suppressResumeIntoPriorHITLGroup &&
        !clearedForSubAgentResumePass &&
        processedPartIndex === narrativeParts.length
      ) {
        flushSubAgentGroup({ messageIndex: uiMessageIndex });
        lastHITLSubAgentGroup = null;
        lastHITLSubAgentGroupMessageIndex = null;
        clearedForSubAgentResumePass = true;
      }

      processedPartIndex += 1;

      if (processedPart.kind === 'skip') {
        continue;
      }

      const item = createRenderablePart({
        processedMessage,
        processedPart,
        noAssistantHeader: resolvedNoHeader,
        autoCollapseToolCalls,
      });

      if (
        processedMessage.isBackgroundUpdate &&
        processedPart.kind === 'assistant-text'
      ) {
        if (pendingSubAgentGroup) {
          clearResumableHITLSubAgentGroupIfLaterMessage({
            messageIndex: uiMessageIndex,
          });
          flushSubAgentGroup({ messageIndex: uiMessageIndex });
        }

        renderItems.push({ kind: 'part', item });
        continue;
      }

      if (
        processedPart.kind === 'assistant-text' &&
        processedPart.part.type === 'reasoning'
      ) {
        const ownerGroup = findOwnerSubAgentGroup(item);

        if (ownerGroup) {
          ownerGroup.children.push(item);
          continue;
        }
      }

      if (processedPart.kind === 'assistant-text') {
        const groupParentKeys = pendingSubAgentGroup
          ? [
              pendingSubAgentGroup.parent,
              ...pendingSubAgentGroup.continuationParents,
            ].map((groupItem) => groupItem.processedPart.key)
          : [];
        const lastCodeAnalystWriteupKeys = new Set(
          groupParentKeys
            .map((parentKey) => writeupKeyByParent.get(parentKey))
            .filter((key): key is string => Boolean(key)),
        );
        const originMatchesOpenGroup = pendingSubAgentGroup
          ? assistantTextMatchesOpenGroup({
              processedPart,
              parentToolName: getToolNameFromPart(
                pendingSubAgentGroup.parent.processedPart,
              ),
            })
          : true;
        const textPlacement = resolveAssistantTextPlacement({
          processedPart,
          lastCodeAnalystWriteupKeys,
          hasOpenSubAgent: pendingSubAgentGroup !== null,
          groupIsRunning: pendingSubAgentGroup
            ? isSubAgentGroupRunning(pendingSubAgentGroup)
            : false,
          originMatchesOpenGroup,
        });

        if (textPlacement === 'nest' && pendingSubAgentGroup) {
          appendSubAgentAssistantText({
            group: pendingSubAgentGroup,
            item,
          });
          continue;
        }

        clearResumableHITLSubAgentGroupIfLaterMessage({
          messageIndex: uiMessageIndex,
        });
        flushSubAgentGroup({ messageIndex: uiMessageIndex });
        renderItems.push({ kind: 'part', item });
        continue;
      }

      if (isSubAgentPart(processedPart)) {
        if (
          pendingSubAgentGroup &&
          canNestInvestigationInSubAgent({
            group: pendingSubAgentGroup,
            item,
          })
        ) {
          pendingSubAgentGroup.children.push(item);
          continue;
        }

        if (
          pendingSubAgentGroup &&
          canMergeSubAgentContinuation({ group: pendingSubAgentGroup, item })
        ) {
          pendingSubAgentGroup.continuationParents.push(item);
          continue;
        }

        const resumableSubAgentState: {
          group: SubAgentExecutionGroup | null;
        } = {
          group: pendingSubAgentGroup
            ? null
            : suppressResumeIntoPriorHITLGroup
              ? null
              : lastHITLSubAgentGroup &&
                  lastHITLSubAgentGroupMessageIndex !== uiMessageIndex
                ? lastHITLSubAgentGroup
                : null,
        };

        if (
          resumableSubAgentState.group &&
          canMergeSubAgentContinuation({
            group: resumableSubAgentState.group,
            item,
          })
        ) {
          resumableSubAgentState.group.continuationParents.push(item);
          pendingSubAgentGroup = resumableSubAgentState.group;
          pendingSubAgentGroupWasRendered = true;
          continue;
        }

        flushSubAgentGroup({ messageIndex: uiMessageIndex });
        pendingSubAgentGroup = {
          parent: item,
          children: [],
          continuationParents: [],
          hasAttachedHITLResponse: false,
        };
        subAgentGroups.push(pendingSubAgentGroup);
        pendingSubAgentGroupWasRendered = false;
        continue;
      }

      appendPartInStoryOrder({ item, messageIndex: uiMessageIndex });
      appendConfirmationAttachments(item);
    }

    if (
      !stableIsLoading &&
      isFeedbackEnabled &&
      processedMessage.traceId &&
      processedMessage.isLastAssistantMessage
    ) {
      flushSubAgentGroup({ messageIndex: uiMessageIndex });
      renderItems.push({ kind: 'feedback', processedMessage });
    }
  }

  flushSubAgentGroup({
    messageIndex: processedMessages.at(-1)?.index ?? 0,
  });

  return markDeliveredBackgroundSubAgents(
    consolidateReadSearchRenderItems(
      mergeAdjacentReasoningRenderItems(renderItems),
    ),
  );
}

function normalizeAgentType(value: string | undefined): string {
  return value?.replace(/_/g, '-').toLowerCase() ?? '';
}

export function markDeliveredBackgroundSubAgents(
  items: ChatRenderItem[],
): ChatRenderItem[] {
  const delivered = items.flatMap((item, index) =>
    item.kind === 'background-update-separator'
      ? [{ index, agentType: normalizeAgentType(item.agentType) }]
      : [],
  );

  if (delivered.length === 0) {
    return items;
  }

  const deliveredGroupIndexes = new Set<number>();

  for (const update of delivered) {
    const matchIndex = items.findIndex((item, index) => {
      if (
        index >= update.index ||
        deliveredGroupIndexes.has(index) ||
        item.kind !== 'sub-agent-group'
      ) {
        return false;
      }

      return (
        normalizeAgentType(
          getToolNameFromPart(item.group.parent.processedPart),
        ) === update.agentType
      );
    });

    if (matchIndex >= 0) {
      deliveredGroupIndexes.add(matchIndex);
    }
  }

  if (deliveredGroupIndexes.size === 0) {
    return items;
  }

  return items.map((item, index) => {
    if (!deliveredGroupIndexes.has(index) || item.kind !== 'sub-agent-group') {
      return item;
    }

    return {
      ...item,
      group: { ...item.group, resultDelivered: true },
    };
  });
}
